import type { MonthPickerProps } from '@ksyos/design-system/dist/components/month-picker';
import type { JSX } from 'react';
import { DateTime } from '@ksyos/date-time';
import {
    Box,
    CheckboxGroupWithTextInput,
    Col,
    FormGroup,
    MonthPicker,
    NumberInput,
    RadioGroup,
    RadioGroupWithTextInput,
    Row,
    Select,
    Text,
    TextArea,
    TextInput,
} from '@ksyos/design-system';
import { memo } from 'react';
import type { IDateInputProps } from '@frontend/components/fields';
import type { BooleanRadioGroupFormGroupFieldProps } from '@frontend/components/fields/BooleanRadioGroupField';
import type { OmittedFormGroupFieldProps } from '@frontend/components/fields/FormGroup';
import type {
    NumberInputFieldProps,
    NumberInputFormGroupFieldProps,
} from '@frontend/components/fields/NumberInputField';
import type { RadioGroupFormGroupFieldProps } from '@frontend/components/fields/RadioGroupField';
import type {
    SelectCountryFormGroupFieldProps,
    SelectFormGroupFieldProps,
} from '@frontend/components/fields/SelectField';
import type { TextAreaFormGroupFieldProps } from '@frontend/components/fields/TextAreaField';
import type { TextInputFieldProps, TextInputFormGroupFieldProps } from '@frontend/components/fields/TextInputField';
import type { Icd10SelectProps } from '@frontend/formEngine/editForm/Icd10Select';
import type { ProcessActivityEditContextMergedProps, ValidationErrors } from '@frontend/utils';
import type {
    CheckboxGroupWithTextInputFormGroupFieldProps,
    NestedCheckboxGroupWithTextInputFormGroupFieldProps,
} from '@frontend/utils/checkboxGroupUtils';
import { nonNullable } from '@common/utils/nonNullableTypeUtils';
import { IndependentDateInput } from '@frontend/components/fields';
import { BooleanRadioGroup } from '@frontend/components/fields/BooleanRadioGroup';
import { computeBooleanRadioGroupProps } from '@frontend/components/fields/BooleanRadioGroupField';
import { computeFormGroupProps } from '@frontend/components/fields/FormGroup';
import { computeNumberInputProps } from '@frontend/components/fields/NumberInputField';
import { computeQuantityProps, type QuantityFormGroupFieldProps } from '@frontend/components/fields/QuantityField';
import { computeRadioGroupProps } from '@frontend/components/fields/RadioGroupField';
import { computeSelectProps } from '@frontend/components/fields/SelectField';
import { computeTextAreaProps } from '@frontend/components/fields/TextAreaField';
import { computeTextInputProps } from '@frontend/components/fields/TextInputField';
import { Icd10Select } from '@frontend/formEngine/editForm/Icd10Select';
import { NestedCheckboxGroupWithTextInput } from '@frontend/formEngine/editForm/NestedCheckboxGroupWithTextInput';
import {
    getValidationErrorTranslation,
    useProcessActivityEditContext,
    useProcessActivityEditContextMergedProps,
} from '@frontend/utils';
import { computeCheckboxGroupWithTextInputProps } from '@frontend/utils/checkboxGroupUtils';
import { getCountryOptions } from '@frontend/utils/countriesUtils';
import {
    computeRadioGroupWithTextInputProps,
    type RadioGroupWithTextInputFormGroupFieldProps,
} from '@frontend/utils/radioGroupUtils';
import { Quantity } from './Quantity';

export function RadioGroupWithTextInputFormGroup(
    props: ProcessActivityEditContextMergedProps<RadioGroupWithTextInputFormGroupFieldProps>
) {
    const properties = useProcessActivityEditContextMergedProps(props);

    return (
        <FormGroup {...computeFormGroupProps(properties)}>
            <RadioGroupWithTextInput {...computeRadioGroupWithTextInputProps(properties)} />
        </FormGroup>
    );
}

export function RadioGroupFormGroup(
    props: ProcessActivityEditContextMergedProps<RadioGroupFormGroupFieldProps>
): JSX.Element {
    const properties = useProcessActivityEditContextMergedProps(props);

    return (
        <FormGroup {...computeFormGroupProps(properties)}>
            <RadioGroup {...computeRadioGroupProps({ ...properties, children: props.children })} />
        </FormGroup>
    );
}

export function BooleanRadioGroupFormGroup(
    props: ProcessActivityEditContextMergedProps<BooleanRadioGroupFormGroupFieldProps>
): JSX.Element {
    const properties = useProcessActivityEditContextMergedProps(props);

    return (
        <FormGroup {...computeFormGroupProps(properties)}>
            <BooleanRadioGroup {...computeBooleanRadioGroupProps(properties)} />
        </FormGroup>
    );
}

export function DateInputFormGroup(
    props: ProcessActivityEditContextMergedProps<OmittedFormGroupFieldProps & IDateInputProps>
): JSX.Element {
    const properties = useProcessActivityEditContextMergedProps(props);

    return (
        <FormGroup {...computeFormGroupProps(properties)}>
            <IndependentDateInput
                {...properties}
                hasMaxWidth={true}
                placeholder={properties.placeholder ?? 'dd-mm-jjjj'}
                variant={properties.variant ?? 'outline'}
            />
        </FormGroup>
    );
}

export function Icd10SelectFormGroup(
    props: ProcessActivityEditContextMergedProps<OmittedFormGroupFieldProps & Icd10SelectProps>
): JSX.Element {
    const properties = useProcessActivityEditContextMergedProps(props);
    const { onChange } = useProcessActivityEditContext();

    return (
        <FormGroup {...computeFormGroupProps(properties)}>
            <Icd10Select
                error={getValidationErrorTranslation(properties.error)}
                onChange={(icd10Code) =>
                    onChange(
                        {
                            [nonNullable(props.fieldKey)]: icd10Code,
                        },
                        props.fieldKey
                    )
                }
                specialismCode={props.specialismCode}
                value={properties.value}
            />
        </FormGroup>
    );
}

export function MonthInputFormGroup(
    props: ProcessActivityEditContextMergedProps<OmittedFormGroupFieldProps & MonthPickerProps>
): JSX.Element {
    const properties = useProcessActivityEditContextMergedProps(props);
    const { onChange } = useProcessActivityEditContext<Record<string, string | null>>();

    // TODO: CS-24030 - MonthInput and IndependentDateInput are inconsistent in how they handle dates
    const convertDateToString = (date: Date | null): string | null => {
        return date ? DateTime.from(date).tz('Europe/Amsterdam').format('YYYY-MM-DD') : null;
    };

    return (
        <FormGroup {...computeFormGroupProps(properties)}>
            <MonthPicker
                {...properties}
                error={getValidationErrorTranslation(properties.error)}
                onChange={(d) =>
                    onChange({
                        [nonNullable(props.fieldKey)]: convertDateToString(d),
                    })
                }
                value={properties.value ? DateTime.from(properties.value).toDate() : null}
            />
        </FormGroup>
    );
}

export function DateRangeInputFormGroup(
    props: Readonly<{
        data: Record<string, string | null>;
        errors: ValidationErrors;
        fieldKey: string;
        label: string;
        onChange: (value: Record<string, string | null>, fieldKey: string) => void;
    }>
): JSX.Element {
    const startDateFieldKey = `${props.fieldKey}StartDate`;
    const startDateValue = props.data[startDateFieldKey];
    const endDateFieldKey = `${props.fieldKey}EndDate`;
    const endDateValue = props.data[endDateFieldKey];

    return (
        <FormGroup label={props.label}>
            <Box
                display="flex"
                gap={3}
            >
                <IndependentDateInput
                    error={props.errors.get(startDateFieldKey)}
                    hasMaxWidth={true}
                    maxDate={endDateValue ? DateTime.from(endDateValue).format('YYYY-MM-DD') : null}
                    onChange={async (value) => {
                        props.onChange({ [startDateFieldKey]: value }, startDateFieldKey);
                    }}
                    placeholder="dd-mm-jjjj"
                    value={props.data[startDateFieldKey]}
                    variant="outline"
                />
                <Text
                    paddingTop={2}
                    size="sm"
                >
                    t/m
                </Text>
                <IndependentDateInput
                    error={props.errors.get(endDateFieldKey)}
                    hasMaxWidth={true}
                    minDate={startDateValue ? DateTime.from(startDateValue).format('YYYY-MM-DD') : null}
                    onChange={async (value) => {
                        props.onChange({ [endDateFieldKey]: value }, endDateFieldKey);
                    }}
                    placeholder="dd-mm-jjjj"
                    value={endDateValue}
                    variant="outline"
                />
            </Box>
        </FormGroup>
    );
}

/*
 * The number input has to be memoized so that it works properly with auto saving. The number input works differently
 * on change (see https://ksyos-it.atlassian.net/wiki/spaces/IT/pages/3229319169/Auto-save), which means that we have
 * to prevent any unnecessary re-renders. A custom comparison function is used, because only the value is relevant
 * for now. If more properties are relevant for re-rendering, include them in the comparison function and be sure to
 * double-check the documentation https://react.dev/reference/react/memo#specifying-a-custom-comparison-function
 */
const MemoizedNumberInput = memo(
    NumberInput,
    (prevProps, nextProps) => prevProps.value === nextProps.value && prevProps.error === nextProps.error
);

export function NumberInputContext(props: ProcessActivityEditContextMergedProps<NumberInputFieldProps>): JSX.Element {
    const properties = useProcessActivityEditContextMergedProps(props);

    return <MemoizedNumberInput {...computeNumberInputProps(properties)} />;
}

export function NumberInputColumnRowFormGroup(
    props: ProcessActivityEditContextMergedProps<NumberInputFormGroupFieldProps & { appendedLabel?: string }>
): JSX.Element {
    const properties = useProcessActivityEditContextMergedProps(props);

    return (
        <FormGroup {...computeFormGroupProps(properties)}>
            <Row columnGap={3}>
                <Col span={2}>
                    <MemoizedNumberInput {...computeNumberInputProps(properties)} />
                </Col>
                {props.appendedLabel && (
                    <Col>
                        <Text
                            fontWeight="medium"
                            mt={2}
                            size="sm"
                        >
                            {props.appendedLabel}
                        </Text>
                    </Col>
                )}
            </Row>
        </FormGroup>
    );
}

export function QuantityFormGroup(
    props: ProcessActivityEditContextMergedProps<QuantityFormGroupFieldProps>
): JSX.Element {
    const properties = useProcessActivityEditContextMergedProps(props);

    return (
        <FormGroup {...computeFormGroupProps(properties)}>
            <Quantity {...computeQuantityProps(properties)} />
        </FormGroup>
    );
}

export function SelectFormGroup(props: ProcessActivityEditContextMergedProps<SelectFormGroupFieldProps>): JSX.Element {
    const properties = useProcessActivityEditContextMergedProps(props);

    return (
        <FormGroup {...computeFormGroupProps(properties)}>
            <Select
                {...computeSelectProps(properties)}
                error={getValidationErrorTranslation(properties.error)}
            />
        </FormGroup>
    );
}

export function SelectWithUnitsFormGroup(
    props: ProcessActivityEditContextMergedProps<SelectFormGroupFieldProps>
): JSX.Element {
    const properties = useProcessActivityEditContextMergedProps(props);

    return (
        <FormGroup {...computeFormGroupProps(properties)}>
            <Row columnGap={3}>
                <Col span={2}>
                    <Select
                        {...computeSelectProps(properties)}
                        error={getValidationErrorTranslation(properties.error)}
                    />
                </Col>
                {props.units && (
                    <Col>
                        <Text
                            fontWeight="medium"
                            mt={2}
                            size="sm"
                        >
                            {props.units}
                        </Text>
                    </Col>
                )}
            </Row>
        </FormGroup>
    );
}

export function SelectCountryFormGroup(
    props: ProcessActivityEditContextMergedProps<SelectCountryFormGroupFieldProps>
): JSX.Element {
    const properties = useProcessActivityEditContextMergedProps(props);

    const options = getCountryOptions();

    return (
        <FormGroup {...computeFormGroupProps(properties)}>
            <Select
                {...computeSelectProps({ ...properties, options })}
                error={getValidationErrorTranslation(properties.error)}
            />
        </FormGroup>
    );
}

export function TextAreaFormGroup(
    props: ProcessActivityEditContextMergedProps<TextAreaFormGroupFieldProps>
): JSX.Element {
    const properties = useProcessActivityEditContextMergedProps(props);

    return (
        <FormGroup {...computeFormGroupProps(properties)}>
            <TextArea {...computeTextAreaProps(properties)} />
        </FormGroup>
    );
}

export function TextInputField(props: ProcessActivityEditContextMergedProps<TextInputFieldProps>): JSX.Element {
    const properties = useProcessActivityEditContextMergedProps(props);

    return <TextInput {...computeTextInputProps(properties)} />;
}

export function TextInputFormGroup(
    props: ProcessActivityEditContextMergedProps<TextInputFormGroupFieldProps>
): JSX.Element {
    const properties = useProcessActivityEditContextMergedProps(props);

    return (
        <FormGroup {...computeFormGroupProps(properties)}>
            <TextInput {...computeTextInputProps(properties)} />
        </FormGroup>
    );
}

export function NarrowerTextInputWithUnitsFormGroup(
    props: ProcessActivityEditContextMergedProps<TextInputFormGroupFieldProps> & { units?: string }
): JSX.Element {
    const { units, ...inputProperties } = props;
    const properties = useProcessActivityEditContextMergedProps(inputProperties);

    return (
        <FormGroup {...computeFormGroupProps(properties)}>
            <Box
                display="grid"
                gridTemplateColumns={['1fr', 'max-content max-content']}
                sx={{ columnGap: 4 }}
            >
                <TextInput {...computeTextInputProps(properties as TextInputFieldProps)} />
                {props.units && (
                    <Text
                        paddingTop={2}
                        size="sm"
                    >
                        {props.units}
                    </Text>
                )}
            </Box>
        </FormGroup>
    );
}

export function CheckboxGroupWithTextInputFormGroup(
    props: ProcessActivityEditContextMergedProps<CheckboxGroupWithTextInputFormGroupFieldProps>
) {
    const properties = useProcessActivityEditContextMergedProps(props);

    return (
        <FormGroup {...computeFormGroupProps(properties)}>
            <CheckboxGroupWithTextInput {...computeCheckboxGroupWithTextInputProps(properties)} />
        </FormGroup>
    );
}

export function NestedCheckboxGroupWithTextInputFormGroup(
    props: ProcessActivityEditContextMergedProps<NestedCheckboxGroupWithTextInputFormGroupFieldProps>
): JSX.Element {
    const properties = useProcessActivityEditContextMergedProps(props);

    return (
        <FormGroup {...computeFormGroupProps(properties)}>
            <NestedCheckboxGroupWithTextInput {...properties} />
        </FormGroup>
    );
}
